import styled from 'styled-components';
import { Card } from '@shared/ui/Card';

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  & > * {
    min-width: 0;
  }
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const RatesSubtext = styled.div`
  font-size: 12px;
  opacity: 0.75;
  margin-top: 2px;
`;

export const KpiStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(200px, 100%), 1fr));
  gap: 12px;
`;

export const KpiCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const KpiCardLabel = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

export const KpiCardValue = styled.div`
  font-size: 20px;
  font-weight: 800;
`;

export const KpiSuccessText = styled.div`
  font-size: 12px;
  color: #10b981;
`;

export const KpiSubtext = styled.div`
  font-size: 12px;
  opacity: 0.75;
`;

export const KpiWarningValue = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #f59e0b;
`;

export const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(230px, 100%), 1fr));
  gap: 12px;
`;

export const MonthBox = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const MonthBoxHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const MonthBoxTitle = styled.strong`
  font-size: 15px;
`;

export const MonthTotalLabel = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

export const MonthTotal = styled.div`
  font-size: 20px;
  font-weight: 800;
`;

export const MonthDetailRow = styled.div`
  font-size: 12px;
  display: flex;
  justify-content: space-between;
`;

export const MonthFooterRow = styled.div`
  font-size: 11px;
  padding-top: 6px;
  border-top: 1px dashed rgba(148, 163, 184, 0.25);
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 8px;
`;

export const MonthPaidText = styled.span`
  color: #10b981;
`;

/** Обе полосы прокрутки — в пределах окна, шапка таблицы остаётся на месте. */
export const TableWrap = styled.div`
  max-height: min(70vh, calc(100vh - 260px));
  overflow: auto;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    padding: 11px 13px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  th {
    position: sticky;
    top: 0;
    z-index: 1;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.colors.text.muted};
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  tfoot td {
    font-weight: 700;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    border-bottom: none;
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }
  td.money {
    white-space: nowrap;
  }
`;

export const FormBox = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FormTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(210px, 100%), 1fr));
  gap: 10px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

/** Поле формы: подпись над полем, ошибка или подсказка под ним. */
export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  & > input,
  & > select {
    width: 100%;
    min-width: 0;
  }
`;

export const FormLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const FieldError = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

export const FieldHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Итог автографика до записи: сколько платежей, первая и последняя дата. */
export const SchedulePreviewText = styled.div<{ $error?: boolean }>`
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme, $error }) =>
    $error ? theme.colors.status.danger : theme.colors.text.secondary};
`;

export const Input = styled.input`
  padding: 7px 11px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;

export const Select = styled.select`
  min-width: 0;
  max-width: 100%;
  padding: 7px 11px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;

export const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  cursor: pointer;
`;

export const FormErrorMessage = styled.div`
  font-size: 12px;
  color: #ef4444;
  font-weight: 600;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

export const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const ContractNumberText = styled.div`
  font-weight: 700;
  margin-top: 4px;
`;

export const MutedDateText = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

export const CreditorNameText = styled.div`
  font-weight: 700;
`;

export const MutedBankText = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

export const TitleCell = styled.td`
  max-width: 340px;
`;

export const TitleText = styled.div`
  font-weight: 600;
`;

export const LeasedAssetText = styled.div`
  font-size: 12px;
  color: #38bdf8;
  margin-top: 2px;
`;

export const LimitText = styled.div`
  font-weight: 800;
`;

export const LimitSubtext = styled.div`
  font-size: 11px;
  opacity: 0.75;
`;

export const RateText = styled.div`
  font-weight: 800;
`;

export const RateSubtext = styled.div`
  font-size: 11px;
  opacity: 0.75;
`;

export const NextDateText = styled.div`
  font-weight: 700;
`;

export const NextAmountText = styled.div`
  font-size: 12px;
`;

export const DueSubtext = styled.div`
  font-size: 11px;
  opacity: 0.75;
`;

export const CreditorContractText = styled.div`
  font-weight: 700;
`;

export const InstrumentTitleText = styled.div`
  font-size: 12px;
  opacity: 0.75;
`;

export const ActionButtonsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

/** Бейджи статуса строки графика: «Просрочено» и «Частично оплачено» рядом. */
export const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

/** Дата оплаты и документ под статусом; без документа — «основание не указано». */
export const PaymentNoteText = styled.div`
  font-size: 11px;
  margin-top: 3px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

// ── Окна отметки оплаты и снятия отметки ──

export const DialogSummary = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** Сумма и дата в одну строку; на узком экране — друг под другом. */
export const DialogFieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(3)};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const DialogError = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;
